'use client'
import { LegacyRef, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Avatar, Box, Flex, Stack, Text, StackProps } from '@chakra-ui/react'
import dayjs from '@/lib/dayjs'
import reactStringReplace from 'react-string-replace'
import { FeedListItem } from '@/types/feed'
import { Images } from './Images'
import { SitePreview } from './SitePreview'

type SummarizedCardProps = {
  feed: FeedListItem
} & StackProps

const REGEXP_URL = /(https?:\/\/\S+)/g
const REGEXP_HASH = /(\s+|^)#[a-zA-Z0-9亜-熙ぁ-んァ-ヶー-龥朗-鶴.\-_]+/g
const REGEXP_URL_OR_HASH =
  /(https?:\/\/\S+|\s+#[a-zA-Z0-9亜-熙ぁ-んァ-ヶー-龥朗-鶴.\-_]+|^#[a-zA-Z0-9亜-熙ぁ-んァ-ヶー-龥朗-鶴.\-_]+)/g

export const SummarizedCard = ({ feed, ...stackProps }: SummarizedCardProps) => {
  const TextRef = useRef<HTMLParagraphElement>()
  const router = useRouter()

  const hasTextOnly = feed.images.length === 0 && !feed.site_preview

  return (
    <Stack {...stackProps} direction={'row'} as="article">
      <Box px={2} paddingRight={4} py={4} w={'100%'} key={feed.id}>
        <Flex mb={4} justifyContent={'space-between'}>
          <Link href={`/companies/${feed.branch_id}`}>
            <Flex alignItems={'center'}>
              <Avatar mr={3} size={['xs', 'xs', 'sm']} src={feed.company_thumbnail_url} />
              <Text fontSize={['xs', 'xs', 'sm']} fontWeight={600}>
                {feed.company_name}
              </Text>
            </Flex>
          </Link>
        </Flex>
        <Link href={`/feeds/${feed.id}`}>
          <Text
            ref={TextRef as LegacyRef<HTMLParagraphElement>}
            fontSize={['xs', 'xs', 'sm']}
            noOfLines={hasTextOnly ? 8 : 3}
          >
            {reactStringReplace(feed.body, REGEXP_URL_OR_HASH, (match, i) => {
              if (match.match(REGEXP_URL)) {
                return (
                  <Box
                    as="span"
                    key={i}
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(match, '_blank')
                    }}
                    color={'blue.500'}
                  >
                    {match}
                  </Box>
                )
              } else if (match.match(REGEXP_HASH)) {
                return (
                  <Text
                    key={i}
                    cursor={'pointer'}
                    as={'span'}
                    color={'red.500'}
                    onClick={(e) => {
                      e.preventDefault()
                      // NOTE: マルチバイト文字(？)がURLに含まれていると、server actionでエラーになるため、二重でエンコードしている
                      router.push(`/feeds?hashtag=1&q=${match.replace(/\n/g, '').replace(/#|　|\s/g, '')}`)
                    }}
                  >
                    {match}
                  </Text>
                )
              }
            })}
          </Text>
          {feed.images.length > 0 ? (
            <Images size="sm" images={feed.images} />
          ) : (
            feed.site_preview && <SitePreview sitePreview={feed.site_preview} size="sm" />
          )}
          <Box pt={1}>
            <Text fontSize={['2xs', '2xs', 'xs']} color={'gray.500'}>
              {dayjs.unix(feed.created_at).fromNow()}
            </Text>
          </Box>
        </Link>
      </Box>
    </Stack>
  )
}
